/* ============================================================================
 * useInitializationEffects.ts - Initialization and setup effects
 * ============================================================================ */

import { useEffect, useLayoutEffect, useRef, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { ProjectData, NodeOperation } from '@/shared/types/projectData';
import type { ApplicationSnapshot, GlobalUIState, ViewStates } from '@/shared/types/applicationState';
import type { SnapshotHistory } from '@/shared/utils/snapshotHistory';
import { storage } from '@/shared/utils/storage';
import { WebSocketManager } from '@/shared/api/WebSocketManager';
import { viewStates, uiState } from '../constants';
import { shouldAdoptAuthoritativeProjectData } from './authoritativeProjectAdoption';

const DEFAULT_VIEW_STATES: ViewStates = {
  gantt: { zoom: 1, scrollPosition: { x: 0, y: 0 }, expandedGroups: [], dateRange: { start: '', end: '' }, showWeekends: true, showDependencies: true, rowHeight: 'normal' },
  kanban: { columnWidths: {}, collapsedColumns: [], sortOrder: {}, showEmptyColumns: true, cardSize: 'normal' },
  calendar: { view: 'month', currentDate: '', selectedCalendars: [], showWeekNumbers: false, firstDayOfWeek: 1, workingHours: { start: '09:00', end: '18:00' } },
  dashboard: { activeWidgets: [], zoom: 1, panPosition: { x: 0, y: 0 } },
  documents: { openDocuments: [], activeDocument: null, editorStates: {}, sidebarWidth: 280, viewMode: 'edit' },
  reports: { selectedReport: '', dateRange: { start: '', end: '' }, groupBy: 'status', chartType: 'bar', filters: {} },
  settings: { topTab: 'general', scrollPosition: 0 },
};

const DEFAULT_UI_STATE: GlobalUIState = {
  activeView: 'dashboard', sidebarCollapsed: false, sidebarWidth: 280, theme: 'system', recentlyUsedEmojis: [], commandPaletteOpen: false, notificationsPanelOpen: false, globalSearchQuery: '', pinnedViews: [],
};

interface UseInitializationEffectsParams {
  authoritativeProjectData: ProjectData | null | undefined;
  selectedProjectIds: string[];
  user: { id?: string } | null;
  wsState: string;
  setProjectData: Dispatch<SetStateAction<ProjectData | null>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setIsCompactView: Dispatch<SetStateAction<boolean>>;
  snapshotHistory: MutableRefObject<SnapshotHistory>;
  sessionId: MutableRefObject<string>;
  wsManager: MutableRefObject<WebSocketManager | null>;
  pollTimer: MutableRefObject<NodeJS.Timeout | null>;
  updateUndoStateFromSnapshots: () => void;
  fetchDataFromServer: () => void;
  syncWithServer: () => Promise<void>;
}

/**
 * Hook for initialization and setup effects
 */
export function useInitializationEffects({
  authoritativeProjectData,
  selectedProjectIds,
  user,
  wsState,
  setProjectData,
  setIsLoading,
  setError,
  setIsCompactView,
  snapshotHistory,
  sessionId,
  wsManager,
  pollTimer,
  updateUndoStateFromSnapshots,
  fetchDataFromServer,
  syncWithServer
}: UseInitializationEffectsParams) {
  const hasInitializedExpansion = useRef(false);
  const previousProjectIds = useRef<string[]>([]);
  const hasBootstrappedProjectDataFromStorage = useRef(false);
  const syncWithServerRef = useRef(syncWithServer);
  useLayoutEffect(() => {
    syncWithServerRef.current = syncWithServer;
  }, [syncWithServer]);

  useEffect(() => {
    if (authoritativeProjectData) {
      const projectsChanged = JSON.stringify(selectedProjectIds) !== JSON.stringify(previousProjectIds.current);

      setProjectData(prevData => {
        // Canonical MultiProject data must win once it diverges from the local
        // watcher snapshot. WebSocket/cache writes can stamp newer local
        // references while still holding stale visibility projection, so a
        // narrow snapshot key can block authoritative refreshes from clearing
        // hidden content out of the UI.
        const shouldAdopt = shouldAdoptAuthoritativeProjectData({
          previousData: prevData,
          authoritativeData: authoritativeProjectData,
        });
        hasBootstrappedProjectDataFromStorage.current = false;
        return shouldAdopt ? authoritativeProjectData : prevData;
      });
      setIsLoading(false);
      setError(null);

      if (!hasInitializedExpansion.current || projectsChanged) {
        hasInitializedExpansion.current = true;
        previousProjectIds.current = [...selectedProjectIds];
      }

      const appState = storage.load();
      if (appState?.hasUserPreferences && appState.settings?.isCompactView !== undefined) {
        setIsCompactView(appState.settings.isCompactView);
      } else {
        setIsCompactView(false);
      }
    } else if (selectedProjectIds.length === 0) {
      setProjectData(null);
      setIsLoading(false);
      setError(null);
      hasInitializedExpansion.current = false;
      hasBootstrappedProjectDataFromStorage.current = false;
      previousProjectIds.current = [];
    }
  }, [authoritativeProjectData, selectedProjectIds, setError, setIsCompactView, setIsLoading, setProjectData]);

  useEffect(() => {
    const shouldBootstrapFromStorage =
      authoritativeProjectData === undefined || (authoritativeProjectData === null && selectedProjectIds.length > 0);
    if (!shouldBootstrapFromStorage) return;

    if (!hasInitializedExpansion.current) {
      const appState = storage.load();
      const storageOwnerId = appState && 'projectStateUserId' in appState
        ? appState.projectStateUserId
        : null;
      const selectedProjectIdSet = new Set(
        selectedProjectIds.filter((projectId) => typeof projectId === 'string' && projectId.trim() && !projectId.includes(',')),
      );
      const belongsToCurrentUser = typeof storageOwnerId === 'string' && storageOwnerId === user?.id;
      const belongsToSelectedProject = Boolean(appState?.projectData?.projectId && selectedProjectIdSet.has(appState.projectData.projectId));
      if (appState && belongsToCurrentUser && belongsToSelectedProject) {
        hasBootstrappedProjectDataFromStorage.current = true;
        setProjectData(appState.projectData);

        if (appState.hasUserPreferences && appState.settings?.isCompactView !== undefined) {
          setIsCompactView(appState.settings.isCompactView);
        } else {
          setIsCompactView(false);
        }
        setIsLoading(false);
        hasInitializedExpansion.current = true;

        const initialSnapshot: ApplicationSnapshot = {
          projectData: appState.projectData,
          viewStates: viewStates ?? DEFAULT_VIEW_STATES,
          uiState: uiState ?? DEFAULT_UI_STATE,
          treeState: {
            expandedNodeIds: appState.expandedNodeIds || [],
            selectedNodeIds: [],
            scrollPosition: 0
          },
          filterState: {
            filters: {},
            searchQuery: '',
            sortBy: null,
            isCompactView: appState.settings?.isCompactView || false
          },
          metadata: {
            timestamp: Date.now(),
            version: '1.0.0',
            sessionId: sessionId.current,
            userId: user?.id
          }
        };

        const initialOperation: NodeOperation = {
          type: 'initial-load',
          // i18n-raw-ui-literal-exempt: Persisted snapshot-history operation semantic, localized at display time.
          description: 'Initial project load from storage',
          skipHistory: false
        };

        snapshotHistory.current.addSnapshot(initialSnapshot, initialOperation, {
          hasDataChanges: true,
          hasViewStateChanges: false,
          hasUIStateChanges: false,
          affectedFeatures: []
        });

        updateUndoStateFromSnapshots();
      } else {
        hasBootstrappedProjectDataFromStorage.current = false;
        fetchDataFromServer();
      }
    }
  }, [authoritativeProjectData, selectedProjectIds, user, setProjectData, setIsCompactView, setIsLoading,
      snapshotHistory, sessionId, updateUndoStateFromSnapshots, fetchDataFromServer]);

  useEffect(() => {
    return () => {
      // The websocket singleton owns the realtime lifecycle. Disconnecting it
      // here drops in-flight queued mutations during provider churn.
      wsManager.current = null;
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [wsManager, pollTimer]);

  useEffect(() => {
    if (wsState === 'disconnected' && !pollTimer.current) {
      pollTimer.current = setInterval(() => {
        void syncWithServerRef.current();
      }, 30000);
    } else if (wsState === 'connected' && pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, [wsState, pollTimer]);
}
